import { useState, useRef, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, Edit2, Palette } from 'lucide-react';
import AIContextMenu from './AIContextMenu';
import ColorPalette from './ColorPalette';
import { contentIcons } from '../utils/iconMap';

export default function EditableBlock({ 
  block, 
  onUpdate, 
  onDelete, 
  onAIOperation,
  isAILoading = false 
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(block.content || '');
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedText, setSelectedText] = useState('');
  const [colorPaletteOpen, setColorPaletteOpen] = useState(false);
  const [colorPalettePosition, setColorPalettePosition] = useState({ x: 0, y: 0 });
  const contentRef = useRef(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  useEffect(() => {
    setContent(block.content || '');
  }, [block.content]);

  const handleContentChange = (e) => {
    const newContent = e.target.textContent || '';
    setContent(newContent);
    onUpdate(block.id, { ...block, content: newContent });
  };

  const handleDoubleClick = () => {
    setIsEditing(true);
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.focus();
        const range = document.createRange();
        range.selectNodeContents(contentRef.current);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, 0);
  };

  const handleBlur = () => {
    setIsEditing(false);
    onUpdate(block.id, { ...block, content });
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    const selection = window.getSelection();
    const selected = selection.toString().trim();

    if (selected) {
      setSelectedText(selected);
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
      });
    }
  };

  const handleAISelect = async (operation) => {
    if (!selectedText) return;

    setContextMenu(null);
    
    if (operation === 'tone') {
      // For tone change, we might want a submenu, but for now just use 'professional'
      await onAIOperation(block.id, 'tone', selectedText, 'professional');
    } else {
      await onAIOperation(block.id, operation, selectedText);
    }
    
    setSelectedText('');
  };

  const IconComponent = contentIcons[block.type] || contentIcons.text;

  const renderBlockContent = () => {
    switch (block.type) {
      case 'header':
        const HeaderTag = `h${block.props?.level || 1}`;
        const headerColor = block.props?.color || '#1E5BB5';
        return (
          <HeaderTag
            ref={contentRef}
            contentEditable={isEditing}
            onBlur={handleBlur}
            onInput={handleContentChange}
            onDoubleClick={handleDoubleClick}
            onContextMenu={handleContextMenu}
            className="outline-none"
            style={{ 
              fontSize: block.props?.fontSize || '24px', 
              textAlign: block.props?.align || 'left',
              color: headerColor
            }}
            suppressContentEditableWarning
          >
            {content || block.defaultContent || 'Heading'}
          </HeaderTag>
        );

      case 'button':
        const buttonColor = block.props?.color || '#1E5BB5';
        return (
          <div className="flex items-center gap-2">
            <a
              href={block.props?.url || '#'}
              className="px-6 py-3 text-white rounded-md font-medium inline-block transition-opacity hover:opacity-90"
              style={{ backgroundColor: buttonColor }}
            >
              <span
                ref={contentRef}
                contentEditable={isEditing}
                onBlur={handleBlur}
                onInput={handleContentChange}
                onDoubleClick={handleDoubleClick}
                onContextMenu={handleContextMenu}
                className="outline-none"
                suppressContentEditableWarning
              >
                {content || block.defaultContent || 'Button Text'}
              </span>
            </a>
          </div>
        );

      case 'image':
        return (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            {block.props?.src ? (
              <img src={block.props.src} alt={content} className="max-w-full h-auto rounded" />
            ) : (
              <div className="text-gray-400">
                <IconComponent size={48} className="mx-auto mb-2" />
                <p>Image placeholder</p>
                <p className="text-sm mt-1">Double-click to edit</p>
              </div>
            )}
          </div>
        );

      case 'divider':
        return <hr className="border-gray-300 my-4" />;

      case 'spacer':
        return <div style={{ height: `${block.props?.height || 20}px` }} />;

      default:
        const textColor = block.props?.color || '#1F2937';
        return (
          <p
            ref={contentRef}
            contentEditable={isEditing}
            onBlur={handleBlur}
            onInput={handleContentChange}
            onDoubleClick={handleDoubleClick}
            onContextMenu={handleContextMenu}
            className="outline-none"
            style={{ color: textColor }}
            suppressContentEditableWarning
          >
            {content || block.defaultContent || 'Text content...'}
          </p>
        );
    }
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className="group relative bg-white border border-gray-200 rounded-lg p-4 mb-2 hover:border-blue-300 transition-colors"
      >
        <div className="flex items-start gap-2">
          <button
            {...attributes}
            {...listeners}
            className="mt-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
          >
            <GripVertical size={16} />
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <IconComponent size={16} className="text-gray-400" />
              <span className="text-xs font-medium text-gray-500 uppercase">{block.type}</span>
            </div>
            {renderBlockContent()}
          </div>

          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {(block.type === 'header' || block.type === 'text' || block.type === 'button') && (
              <button
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setColorPalettePosition({ x: rect.left, y: rect.bottom + 5 });
                  setColorPaletteOpen(true);
                }}
                className="p-1 text-gray-400 hover:text-purple-600"
                title="Change Color"
              >
                <Palette size={14} />
              </button>
            )}
            <button
              onClick={() => setIsEditing(true)}
              className="p-1 text-gray-400 hover:text-blue-600"
              title="Edit"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => onDelete(block.id)}
              className="p-1 text-gray-400 hover:text-red-600"
              title="Delete"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>

      {contextMenu && (
        <AIContextMenu
          position={contextMenu}
          onClose={() => setContextMenu(null)}
          onSelect={handleAISelect}
          selectedText={selectedText}
          isLoading={isAILoading}
        />
      )}

      {colorPaletteOpen && (
        <ColorPalette
          isOpen={colorPaletteOpen}
          onClose={() => setColorPaletteOpen(false)}
          onSelectColor={(color) => {
            const updatedProps = { ...block.props, color };
            onUpdate(block.id, { ...block, props: updatedProps });
          }}
          currentColor={block.props?.color || (block.type === 'header' ? '#1E5BB5' : block.type === 'button' ? '#1E5BB5' : '#1F2937')}
          position={colorPalettePosition}
        />
      )}
    </>
  );
}

